<template>
    <div class="ppv2-space-y-6">
        <!-- Langue -->
        <BaseSelect
            v-model="formData.language"
            :label="$t('configuration.locationForm.fields.language.label')"
            :options="languageOptions"
            required
            :error="errors.language"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5h12M9 3v2m1.048.5A18.077 18.077 0 0112 10.5c.525 1.757 1.158 3.515 1.683 5.257M9 12a2.25 2.25 0 002.25 2.25c.907 0 1.648-.743 1.648-1.648 0-.907-.743-1.648-1.648-1.648A2.25 2.25 0 009 12zm0 0l3 3m0-3l-3-3"
                    />
                </svg>
            </template>
        </BaseSelect>

        <!-- Time zone -->
        <BaseSelect
            v-model="formData.timezone"
            :label="$t('configuration.locationForm.fields.timezone.label')"
            :tooltipMsg="$t('configuration.locationForm.fields.timezone.description')"
            :options="timezoneOptions"
            required
            :error="errors.timezone"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
            </template>
        </BaseSelect>

        <!-- Nom de l'établissement -->
        <BaseInput
            v-model="formData.name"
            :label="$t('configuration.generalInformation.fields.establishmentName.label')"
            :tooltipMsg="$t('configuration.generalInformation.fields.establishmentName.description')"
            :placeholder="$t('configuration.generalInformation.fields.establishmentName.placeholder')"
            required
            :error="errors.name"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                </svg>
            </template>
        </BaseInput>

        <!-- Logo de l'établissement -->
        <CustomInputUpload
            v-model="formData.logo"
            :label="$t('configuration.generalInformation.fields.logo.label')"
            :description="$t('configuration.generalInformation.fields.logo.description')"
            :required="true"
            :errorText="errors.logo"
            specificTaille="130×40px"
            accept="image/jpeg,image/jpg,image/png"
            :enableCropper="true"
            :maxSize="1024 * 1024"
            classContainerAdd="!ppv2-mb-0"
        />

        <!-- Nom de la société -->
        <BaseInput
            v-model="formData.company_name"
            :label="$t('configuration.generalInformation.fields.companyName.label')"
            :tooltipMsg="$t('configuration.generalInformation.fields.companyName.description')"
            :placeholder="$t('configuration.generalInformation.fields.companyName.placeholder')"
            required
            :error="errors.company_name"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    ></path>
                </svg>
            </template>
        </BaseInput>

        <!-- Forme juridique -->
        <BaseSelect
            v-model="formData.legal_form"
            :label="$t('configuration.generalInformation.fields.legalForm.label')"
            :tooltipMsg="$t('configuration.generalInformation.fields.legalForm.description')"
            :options="legal_formOptions"
            required
            :error="errors.legal_form"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                </svg>
            </template>
        </BaseSelect>

        <!-- Numéro d'enregistrement -->
        <BaseInput
            v-model="formData.siret"
            :label="$t('configuration.generalInformation.fields.registrationNumber.label')"
            :tooltipMsg="$t('configuration.generalInformation.fields.registrationNumber.description')"
            :placeholder="$t('configuration.generalInformation.fields.registrationNumber.placeholder')"
            required
            :error="errors.siret"
        >
            <template #leftIcon>
                <span class="ppv2-text-gray-400 ppv2-text-sm poppinsMedium">#</span>
            </template>
        </BaseInput>

        <!-- Code d'activité -->
        <!-- <BaseInput
            v-model="formData.activity_code"
            :label="$t('configuration.generalInformation.fields.activityCode.label')"
            :tooltipMsg="$t('configuration.generalInformation.fields.activityCode.description')"
            :placeholder="$t('configuration.generalInformation.fields.activityCode.placeholder')"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    ></path>
                </svg>
            </template>
        </BaseInput> -->

        <!-- Capital social -->
        <BaseInput
            v-model="formData.social_capital"
            :label="$t('configuration.generalInformation.fields.socialCapital.label')"
            :tooltipMsg="$t('configuration.generalInformation.fields.socialCapital.description')"
            :placeholder="$t('configuration.generalInformation.fields.socialCapital.placeholder')"
            type="number"
            :suffix="$symbolCurrency"
        >
            <template #leftIcon>
                <svg
                    class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                    viewBox="0 0 21 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M11.0566 10.6352V11.9881C11.1064 11.9664 11.1562 11.9404 11.2017 11.9123C11.4571 11.7565 11.587 11.5378 11.5891 11.2543C11.5913 10.9512 11.4138 10.7456 11.0588 10.6331L11.0566 10.6352Z"
                        fill="#131C31"
                    />
                    <path
                        d="M18.2754 3.29096H15.3857V2.66107C15.3857 1.33203 14.3034 0.249756 12.9743 0.249756H7.69716C6.36812 0.249756 5.28584 1.33203 5.28584 2.66107V3.29096H2.39615C1.35067 3.29096 0.5 4.14163 0.5 5.18711V14.854C0.5 15.8995 1.35067 16.7502 2.39615 16.7502H18.2775C19.323 16.7502 20.1737 15.8995 20.1737 14.854V5.18711C20.1737 4.14163 19.323 3.29096 18.2775 3.29096H18.2754ZM19.4875 14.854C19.4875 15.5229 18.9442 16.0662 18.2754 16.0662H2.39615C1.7273 16.0662 1.184 15.5229 1.184 14.854V5.18711C1.184 4.51826 1.7273 3.97496 2.39615 3.97496H5.63C5.81832 3.97496 5.972 3.82128 5.972 3.63296V2.66107C5.972 1.70867 6.74691 0.933756 7.69932 0.933756H12.9765C13.9289 0.933756 14.7038 1.70867 14.7038 2.66107V3.63296C14.7038 3.82128 14.8575 3.97496 15.0458 3.97496H18.2775C18.9464 3.97496 19.4897 4.51826 19.4897 5.18711V14.854H19.4875Z"
                        fill="#131C31"
                    />
                    <path
                        d="M12.9744 1.80151H7.69722C7.22318 1.80151 6.83789 2.1868 6.83789 2.66084V3.63273C6.83789 3.82105 6.99157 3.97473 7.17989 3.97473H8.48945C8.48945 3.97473 8.50244 3.97473 8.50676 3.97473H8.76868C8.76868 3.97473 8.78166 3.97473 8.78599 3.97473H9.20592C9.20592 3.97473 9.2189 3.97473 9.22323 3.97473H11.4419C11.4419 3.97473 11.4549 3.97473 11.4592 3.97473H11.8791C11.8791 3.97473 11.8921 3.97473 11.8965 3.97473H12.1584C12.1584 3.97473 12.1714 3.97473 12.1757 3.97473H13.4852C13.6736 3.97473 13.8272 3.82105 13.8272 3.63273V2.66084C13.8272 2.1868 13.442 1.80151 12.9679 1.80151H12.9744ZM13.1476 3.29073H12.1627C12.1627 3.29073 12.1497 3.29073 12.1454 3.29073H11.9203C11.9203 3.29073 11.9095 3.29073 11.903 3.29073H11.4462C11.4462 3.29073 11.4332 3.29073 11.4289 3.29073H9.24704C9.24704 3.29073 9.23406 3.29073 9.22973 3.29073H8.77301C8.77301 3.29073 8.76218 3.29073 8.75569 3.29073H8.53058C8.53058 3.29073 8.51759 3.29073 8.51326 3.29073H7.52838V2.66084C7.52838 2.5656 7.60631 2.48768 7.70155 2.48768H12.9787C13.074 2.48768 13.1519 2.5656 13.1519 2.66084V3.29073H13.1476Z"
                        fill="#131C31"
                    />
                    <path
                        d="M11.4001 8.90568V8.59831C11.5234 8.66974 11.6641 8.7087 11.8113 8.7087C11.9737 8.7087 12.1403 8.63078 12.188 8.60697C12.1945 8.6048 12.1988 8.60048 12.2053 8.59831C12.2096 8.59615 12.2139 8.59398 12.2183 8.58965L12.9542 8.1286C13.1534 8.00306 13.2637 7.81691 13.2681 7.60045C13.2702 7.42945 13.2031 7.2736 13.0754 7.14806C12.6295 6.64588 12.0667 6.30821 11.4001 6.13937V5.72161C11.4001 5.33632 11.0862 5.02246 10.7009 5.02246H10.045C9.65976 5.02246 9.34373 5.33632 9.34373 5.72161V6.11123C9.08398 6.16751 8.84372 6.24544 8.6251 6.34501C7.36316 6.92078 7.11207 7.81474 7.12506 8.46411C7.13372 8.92949 7.27658 9.34725 7.54715 9.7044C7.93893 10.2282 8.54284 10.581 9.34589 10.7542V11.4512C9.19221 11.3451 9.04069 11.2131 8.8935 11.0551C8.75713 10.8862 8.56449 10.7932 8.34587 10.7932H8.3437C8.23115 10.7932 8.09911 10.8386 8.01036 10.8776C8.00603 10.8776 8.0017 10.8819 7.99737 10.8841C7.98872 10.8884 7.98006 10.8927 7.9714 10.8971L7.30472 11.2889C7.08177 11.4187 6.96055 11.62 6.96704 11.8516C6.96921 11.9967 7.02116 12.1352 7.11856 12.2499C7.31121 12.5162 7.56879 12.7716 7.88049 13.0075C8.31773 13.3452 8.81125 13.5768 9.34806 13.7023V14.0292C9.34806 14.4145 9.66192 14.7305 10.0494 14.7305H10.7052C11.0905 14.7305 11.4044 14.4166 11.4044 14.0292V13.7045C11.8611 13.6071 12.2702 13.4404 12.6209 13.2088C13.3308 12.7391 13.6988 12.0832 13.714 11.2564C13.7248 10.7304 13.5733 10.2737 13.2637 9.89921C12.8438 9.3992 12.2183 9.06369 11.4022 8.90351L11.4001 8.90568ZM12.2399 12.6395C11.8979 12.8647 11.4845 13.014 11.0061 13.0833C10.8373 13.1071 10.7139 13.2521 10.7139 13.4209V14.0314C10.7139 14.0314 10.7074 14.0465 10.6987 14.0465H10.0429C10.0429 14.0465 10.0277 14.04 10.0277 14.0314V13.4231C10.0277 13.2543 9.90652 13.1114 9.73984 13.0854C9.20303 13.001 8.71601 12.7911 8.29175 12.4664C8.0385 12.2737 7.8307 12.0703 7.67918 11.8646L8.29825 11.501C8.31556 11.4945 8.33505 11.488 8.3502 11.4837C8.35453 11.4837 8.35886 11.488 8.35886 11.488C8.36535 11.4967 8.37184 11.5053 8.3805 11.5118C8.74631 11.9101 9.14892 12.1871 9.57317 12.3365C9.67707 12.3733 9.79396 12.3581 9.88487 12.2932C9.97578 12.2283 10.0299 12.1244 10.0299 12.014V10.4707C10.0299 10.3018 9.90652 10.159 9.73984 10.133C8.97359 10.0139 8.42163 9.7347 8.09478 9.2953C7.90863 9.0507 7.81555 8.77364 7.81122 8.45329C7.79824 7.79743 8.15972 7.31256 8.91298 6.9684C9.15325 6.85801 9.43031 6.78225 9.73768 6.73896C9.90651 6.71515 10.0321 6.57012 10.0321 6.39912V5.72161C10.0321 5.72161 10.0386 5.70646 10.0472 5.70646H10.7031C10.7031 5.70646 10.7182 5.71295 10.7182 5.72161V6.41644C10.7182 6.58311 10.8373 6.72597 11.0018 6.75411C11.636 6.8645 12.1403 7.13507 12.5451 7.5788L11.8741 7.99873C11.8546 8.00739 11.8221 8.02037 11.8157 8.02254C11.7789 8.02254 11.755 8.01388 11.7312 7.9944C11.5667 7.8537 11.3914 7.73682 11.2053 7.65024C11.0992 7.60045 10.9758 7.60694 10.8762 7.66972C10.7767 7.73249 10.7161 7.84072 10.7161 7.9576V9.19356C10.7161 9.36456 10.8416 9.50959 11.0104 9.5334C11.7983 9.64379 12.3806 9.91653 12.7356 10.3386C12.9391 10.5854 13.0343 10.8819 13.0278 11.2434C13.017 11.8387 12.7594 12.2932 12.2421 12.6374L12.2399 12.6395Z"
                        fill="#131C31"
                    />
                    <path
                        d="M9.25195 8.4621C9.25195 8.72402 9.39914 8.91233 9.68486 9.02056V7.92529C9.66755 7.93179 9.65023 7.93828 9.63508 7.94477C9.37966 8.06815 9.25195 8.23915 9.25195 8.45994V8.4621Z"
                        fill="#131C31"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Site web officiel -->
        <BaseInput
            v-model="formData.official_website"
            :label="$t('configuration.generalInformation.fields.officialWebsite.label')"
            :placeholder="$t('configuration.generalInformation.fields.officialWebsite.placeholder')"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    ></path>
                </svg>
            </template>
        </BaseInput>

        <!-- Site web officiel actif -->
        <BaseSwitch
            v-model="formData.official_website_is_active"
            :label="$t('configuration.generalInformation.fields.officialWebsiteActive.label')"
            :description="$t('configuration.generalInformation.fields.officialWebsiteActive.description')"
        />
    </div>
</template>

<script>
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'

const REQUIRED_FIELDS = ['name', 'company_name', 'legal_form', 'siret', 'language', 'timezone', 'logo']

export default {
    name: 'GeneralInformationForm',
    components: {
        BaseInput,
        BaseSelect,
        BaseSwitch,
        CustomInputUpload,
    },
    data() {
        return {
            formData: {
                name: '',
                company_name: '',
                legal_form: 'SAS',
                siret: '',
                // activity_code: '',
                social_capital: '',
                official_website: '',
                official_website_is_active: false,
                language: '',
                timezone: 'Europe/Paris',
                logo: '',
            },
            errors: {},
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        legal_formOptions() {
            const forms = ['SAS', 'SARL', 'EI', 'EURL', 'SA', 'SNC', 'SCS', 'MICRO']
            return forms.map((form) => ({
                label: this.$t(`configuration.generalInformation.legalFormOptions.${form}`),
                value: form,
            }))
        },
        languageOptions() {
            return [
                { label: 'Français', value: 'fr' },
                { label: 'English', value: 'en' },
                // { label: 'Deutsch', value: 'de' },
            ]
        },
        timezoneOptions() {
            return [
                { label: 'UTC (Coordinated Universal Time)', value: 'UTC' },
                { label: 'Europe/Paris - UTC+1 (Central)', value: 'Europe/Paris' },
                { label: 'Europe/London - UTC+0 (GMT)', value: 'Europe/London' },
                { label: 'Europe/Berlin - UTC+1 (Central)', value: 'Europe/Berlin' },
                { label: 'Europe/Madrid - UTC+1 (Central)', value: 'Europe/Madrid' },
                { label: 'Europe/Rome - UTC+1 (Central)', value: 'Europe/Rome' },
                { label: 'Europe/Istanbul - UTC+3', value: 'Europe/Istanbul' },
                { label: 'Europe/Moscow - UTC+3', value: 'Europe/Moscow' },
                { label: 'Africa/Cairo - UTC+2', value: 'Africa/Cairo' },
                { label: 'Africa/Johannesburg - UTC+2', value: 'Africa/Johannesburg' },
                { label: 'Africa/Casablanca - UTC+1', value: 'Africa/Casablanca' },
                { label: 'Africa/Tunis - UTC+1', value: 'Africa/Tunis' },
                { label: 'Asia/Dubai - UTC+4', value: 'Asia/Dubai' },
                { label: 'Asia/Riyadh - UTC+3', value: 'Asia/Riyadh' },
                { label: 'Asia/Karachi - UTC+5', value: 'Asia/Karachi' },
                { label: 'Asia/Kolkata - UTC+5:30', value: 'Asia/Kolkata' },
                { label: 'Asia/Bangkok - UTC+7', value: 'Asia/Bangkok' },
                { label: 'Asia/Singapore - UTC+8', value: 'Asia/Singapore' },
                { label: 'Asia/Tokyo - UTC+9', value: 'Asia/Tokyo' },
                { label: 'Asia/Seoul - UTC+9', value: 'Asia/Seoul' },
                { label: 'Asia/Shanghai - UTC+8', value: 'Asia/Shanghai' },
                { label: 'Asia/Hong_Kong - UTC+8', value: 'Asia/Hong_Kong' },
                { label: 'Australia/Sydney - UTC+10', value: 'Australia/Sydney' },
                { label: 'Australia/Melbourne - UTC+10', value: 'Australia/Melbourne' },
                { label: 'Pacific/Auckland - UTC+12', value: 'Pacific/Auckland' },
                { label: 'America/New_York - UTC-5 (Eastern)', value: 'America/New_York' },
                { label: 'America/Chicago - UTC-6 (Central)', value: 'America/Chicago' },
                { label: 'America/Denver - UTC-7 (Mountain)', value: 'America/Denver' },
                { label: 'America/Los_Angeles - UTC-8 (Pacific)', value: 'America/Los_Angeles' },
                { label: 'America/Toronto - UTC-5 (Eastern)', value: 'America/Toronto' },
                { label: 'America/Montreal - UTC-5 (Eastern)', value: 'America/Montreal' },
                { label: 'America/Sao_Paulo - UTC-3', value: 'America/Sao_Paulo' },
                { label: 'America/Mexico_City - UTC-6', value: 'America/Mexico_City' },
            ]
        },
    },
    watch: {
        establishment: {
            handler() {
                this.initializeForm()
            },
            immediate: true,
            deep: true,
        },
        formData: {
            handler(newVal) {
                this.$emit('update', newVal)
                // Clear error when field is updated
                Object.keys(this.errors).forEach((field) => {
                    if (newVal[field]) {
                        this.$delete(this.errors, field)
                    }
                })
            },
            deep: true,
        },
        // NOTE: locale change is applied only after successful save, not on select change
    },
    methods: {
        initializeForm() {
            const defaults = {
                name: '',
                company_name: '',
                legal_form: 'SAS',
                siret: '',
                // activity_code: '',
                social_capital: '',
                official_website: '',
                official_website_is_active: false,
                language: '',
                timezone: 'Europe/Paris',
                logo: '',
            }
            this.formData = { ...defaults, ...this.establishment }
            const savedLocale = localStorage.getItem('locale')
            if (savedLocale && ['en', 'fr'].includes(savedLocale)) {
                this.$i18n.locale = savedLocale
            } else {
                this.$i18n.locale = this.formData.language || 'fr'
                localStorage.setItem('locale', this.$i18n.locale)
            }
        },
        getFormData() {
            return { ...this.formData }
        },
        setFormData(data) {
            this.formData = { ...this.formData, ...data }
        },
        validateForm() {
            this.errors = {}
            let isValid = true
            REQUIRED_FIELDS.forEach((field) => {
                const val = this.formData[field]
                const isEmpty = !val || (typeof val === 'string' && val.trim() === '')
                if (isEmpty) {
                    let errorMsg = ''
                    if (field === 'company_name')
                        errorMsg = this.$t('configuration.generalInformation.fields.companyName.requiredError')
                    else if (field === 'name')
                        errorMsg = this.$t('configuration.generalInformation.fields.establishmentName.requiredError')
                    else if (field === 'legal_form')
                        errorMsg = this.$t('configuration.generalInformation.fields.legalForm.requiredError')
                    else if (field === 'siret')
                        errorMsg = this.$t('configuration.generalInformation.fields.registrationNumber.requiredError')
                    else if (field === 'timezone') errorMsg = this.$t('configuration.locationForm.errors.timezoneRequired')
                    else if (field === 'language')
                        errorMsg = this.$t('configuration.locationForm.fields.language.label') + ' est requis'
                    else if (field === 'logo') errorMsg = this.$t('configuration.generalInformation.fields.logo.requiredError')

                    this.$set(this.errors, field, errorMsg || 'Ce champ est requis')
                    isValid = false
                }
            })
            return isValid
        },
        resetForm() {
            this.formData = {
                name: '',
                company_name: '',
                legal_form: 'SAS',
                siret: '',
                // activity_code: '',
                social_capital: '',
                official_website: '',
                official_website_is_active: false,
                language: '',
                timezone: 'Europe/Paris',
                logo: '',
            }
        },
        getValidationErrors() {
            const errors = {}
            const i18nKeys = {
                company_name: 'configuration.generalInformation.fields.companyName.requiredError',
                name: 'configuration.generalInformation.fields.establishmentName.requiredError',
                legal_form: 'configuration.generalInformation.fields.legalForm.requiredError',
                siret: 'configuration.generalInformation.fields.registrationNumber.requiredError',
                timezone: 'configuration.locationForm.errors.timezoneRequired',
                language: 'configuration.generalInformation.fields.language.requiredError',
                logo: 'configuration.generalInformation.fields.logo.requiredError',
            }
            REQUIRED_FIELDS.forEach((field) => {
                const val = this.formData[field]
                if (!val || (typeof val === 'string' && val.trim() === '')) {
                    errors[field] = this.$t(i18nKeys[field] || `Ce champ est requis`)
                }
            })
            return errors
        },
    },
}
</script>

<style scoped>
/* Add custom styles if needed */
</style>
